import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    vendor_invoice_number: '',
    basic_amount: '',
    tax_amount: '',
    invoice_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
  });

  const totalAmount = (Number(form.basic_amount) || 0) + (Number(form.tax_amount) || 0);

  const fetchInfo = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/vendor-upload/${token}`);
      if (!resp.ok) throw new Error('Link not found or expired');
      const data = await resp.json();
      setInfo(data);
    } catch (e) { setError(e.message || 'Invalid upload link'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleUpload = async () => {
    if (!file) return;
    if (!form.vendor_invoice_number.trim()) { setError('Invoice number is required'); return; }
    setUploading(true);
    setSuccess(null);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vendor_invoice_number', form.vendor_invoice_number);
      formData.append('basic_amount', form.basic_amount || '0');
      formData.append('tax_amount', form.tax_amount || '0');
      formData.append('invoice_amount', String(totalAmount));
      formData.append('invoice_date', form.invoice_date);
      formData.append('description', form.description);
      formData.append('notes', form.notes);
      const resp = await fetch(`${API_URL}/api/vendor-upload/${token}`, { method: 'POST', body: formData });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await resp.json();
      setSuccess(data);
      setFile(null);
      setForm({ vendor_invoice_number: '', basic_amount: '', tax_amount: '', invoice_date: new Date().toISOString().split('T')[0], description: '', notes: '' });
      fetchInfo();
    } catch (e) { setError(e.message); }
    setUploading(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="animate-spin text-muted-foreground" size={24} />
    </div>
  );

  if (error && !info) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full" data-testid="upload-error">
        <CardContent className="p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Link Not Found</h2>
          <p className="text-muted-foreground text-sm">This upload link is invalid or has expired. Please contact the sender for a new link.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="vendor-upload-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>GO MO Group DBS</h1>
          <p className="text-sm text-muted-foreground">Vendor Invoice Upload Portal</p>
        </div>

        {/* PO Info */}
        <Card data-testid="po-info-card">
          <CardHeader>
            <CardTitle className="text-lg">Purchase Order Details</CardTitle>
            <CardDescription>Upload your invoice against this purchase order</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">PO Number</p>
                <p className="text-foreground font-mono font-medium mt-1" data-testid="po-number">{info?.po_number}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Amount</p>
                <p className="text-foreground font-mono font-medium mt-1" data-testid="po-amount">{formatCurrency(info?.amount, info?.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendor</p>
                <p className="text-foreground font-medium mt-1">{info?.vendor_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Currency</p>
                <p className="text-foreground font-medium mt-1">{info?.currency}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Form */}
        <Card data-testid="upload-form-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Upload size={18} /> Upload Invoice</CardTitle>
            <CardDescription>Fill in the invoice details and upload the PDF</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Invoice Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground/80">Invoice Number *</Label>
                <Input value={form.vendor_invoice_number} onChange={e => setForm({ ...form, vendor_invoice_number: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                  data-testid="vendor-invoice-number-input" placeholder="e.g. INV-001" />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Invoice Date</Label>
                <Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                  data-testid="vendor-invoice-date-input" />
              </div>
            </div>
            {/* Amount Breakdown */}
            <div className="space-y-3">
              <Label className="text-foreground font-medium text-sm">Amount Breakdown</Label>
              <div className="grid grid-cols-3 gap-4 p-4 border border-border rounded-lg bg-muted/20">
                <div className="space-y-2">
                  <Label className="text-foreground/80 text-xs uppercase tracking-wider">Basic Amount</Label>
                  <p className="text-[11px] text-muted-foreground -mt-1">Pre-tax invoice value</p>
                  <Input type="number" step="0.01" value={form.basic_amount} onChange={e => setForm({ ...form, basic_amount: e.target.value })}
                    className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                    data-testid="vendor-basic-amount-input" placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80 text-xs uppercase tracking-wider">Tax Amount</Label>
                  <p className="text-[11px] text-muted-foreground -mt-1">GST / VAT / Other taxes</p>
                  <Input type="number" step="0.01" value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: e.target.value })}
                    className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                    data-testid="vendor-tax-amount-input" placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80 text-xs uppercase tracking-wider">Total Invoice Amount</Label>
                  <p className="text-[11px] text-muted-foreground -mt-1">Basic + Tax (auto-calculated)</p>
                  <div className="h-9 flex items-center px-3 rounded-md border border-input bg-muted/40 font-mono font-semibold text-foreground" data-testid="vendor-total-display">
                    {info?.currency || 'USD'} {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                data-testid="vendor-description-input" placeholder="Invoice description or services rendered" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Notes (optional)</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground"
                data-testid="upload-notes-input" placeholder="Payment terms, additional info..." />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label className="text-foreground/80">Invoice PDF *</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => document.getElementById('vendor-file-input').click()}
                data-testid="file-drop-zone">
                <input id="vendor-file-input" type="file" accept=".pdf" className="hidden"
                  onChange={e => setFile(e.target.files[0])} data-testid="file-input" />
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-foreground">
                    <FileText size={20} className="text-blue-500" />
                    <span className="font-medium">{file.name}</span>
                    <Badge variant="outline" className="text-xs">{(file.size / 1024).toFixed(0)} KB</Badge>
                  </div>
                ) : (
                  <div>
                    <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a PDF file</p>
                    <p className="text-xs text-muted-foreground mt-1">Maximum file size: 10MB</p>
                  </div>
                )}
              </div>
            </div>

            {error && info && (
              <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="upload-error-msg">{error}</div>
            )}
            {success && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2" data-testid="upload-success-msg">
                <CheckCircle size={16} />
                <span className="text-sm">Invoice uploaded successfully! Reference: {success.invoice_number}</span>
              </div>
            )}
            <Button onClick={handleUpload} disabled={!file || uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="submit-upload-button">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {uploading ? 'Uploading...' : 'Upload Invoice'}
            </Button>
          </CardContent>
        </Card>

        {/* Previous Uploads */}
        {info?.uploads && info.uploads.length > 0 && (
          <Card data-testid="previous-uploads-card">
            <CardHeader>
              <CardTitle className="text-lg">Previous Uploads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {info.uploads.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 border border-border rounded-md">
                    <div className="flex items-center gap-3">
                      <FileText size={16} className="text-blue-500" />
                      <div>
                        <p className="text-sm text-foreground font-medium">{u.vendor_invoice_number || u.filename}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(u.uploaded_at)} {u.notes && `- ${u.notes}`}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="status-completed text-xs">Uploaded</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">GO MO Group DBS - Vendor Invoice Portal</p>
      </div>
    </div>
  );
}
